/**
 * Official CT600 box numbers, labels and grouping.
 *
 * Labels are HMRC's own wording from the CT600 (2024) v3 form, so the preview
 * a user checks before submitting reads the same as the statutory form and can
 * be compared against it line by line.
 *
 * Only the boxes the backend mapper actually emits are listed. Anything the
 * mapper starts producing later shows up in the preview under "Other boxes"
 * rather than being silently dropped.
 */

export type BoxFormat = 'text' | 'money' | 'date' | 'flag' | 'number'

export interface BoxDefinition {
  box: string
  label: string
  format: BoxFormat
}

export interface BoxSection {
  title: string
  /** Shown under the section heading when the grouping needs explaining. */
  hint?: string
  boxes: BoxDefinition[]
}

export const CT600_BOX_SECTIONS: BoxSection[] = [
  {
    title: 'Company information',
    boxes: [
      { box: '1', label: 'Company name', format: 'text' },
      { box: '2', label: 'Company registration number', format: 'text' },
      { box: '3', label: 'Tax reference', format: 'text' },
      { box: '4', label: 'Type of company', format: 'text' },
      { box: '30', label: 'Start of return', format: 'date' },
      { box: '35', label: 'End of return', format: 'date' },
    ],
  },
  {
    title: 'About this return',
    boxes: [
      { box: '40', label: 'Repayments this period', format: 'flag' },
      { box: '55', label: 'Estimated figures', format: 'flag' },
      { box: '60', label: 'Company part of a group that is not small', format: 'flag' },
      { box: '80', label: 'Accounts and computations attached for the period of this return', format: 'flag' },
      { box: '326', label: 'Number of associated companies in this period', format: 'number' },
    ],
  },
  {
    title: 'Turnover',
    boxes: [{ box: '145', label: 'Total turnover from trade', format: 'money' }],
  },
  {
    title: 'Income',
    boxes: [
      { box: '155', label: 'Trading profits', format: 'money' },
      { box: '160', label: 'Trading losses brought forward against profits', format: 'money' },
      { box: '165', label: 'Net trading profits', format: 'money' },
      {
        box: '170',
        label: 'Bank, building society or other interest, and profits from non-trading loan relationships',
        format: 'money',
      },
      { box: '190', label: 'Income from a property business', format: 'money' },
      { box: '205', label: 'Income not falling under any other heading', format: 'money' },
    ],
  },
  {
    title: 'Chargeable profits',
    boxes: [
      { box: '235', label: 'Profits before other deductions and reliefs', format: 'money' },
      { box: '300', label: 'Profits before qualifying donations and group relief', format: 'money' },
      { box: '305', label: 'Qualifying donations', format: 'money' },
      { box: '315', label: 'Profits chargeable to Corporation Tax', format: 'money' },
    ],
  },
  {
    title: 'Tax calculation',
    boxes: [
      { box: '430', label: 'Corporation Tax', format: 'money' },
      { box: '435', label: 'Marginal relief for ring fence trades', format: 'money' },
      { box: '440', label: 'Corporation Tax chargeable', format: 'money' },
      { box: '450', label: 'Double Taxation Relief', format: 'money' },
      { box: '470', label: 'Total reliefs and deductions in terms of tax', format: 'money' },
      { box: '475', label: 'Net Corporation Tax liability', format: 'money' },
    ],
  },
  {
    title: 'Reliefs and payments',
    boxes: [
      { box: '510', label: 'Tax chargeable', format: 'money' },
      { box: '515', label: 'Income Tax deducted from gross income included in profits', format: 'money' },
      { box: '528', label: 'Self-assessment of tax payable', format: 'money' },
      { box: '530', label: 'Research and Development credit', format: 'money' },
      { box: '595', label: 'Tax already paid (and not already repaid)', format: 'money' },
      { box: '600', label: 'Tax outstanding', format: 'money' },
      { box: '605', label: 'Tax overpaid including surplus or payable credits', format: 'money' },
    ],
  },
  {
    title: 'Research and development',
    boxes: [
      {
        box: '650',
        label: 'Claim made by a small or medium-sized enterprise (SME), including an SME subcontractor to a large company',
        format: 'flag',
      },
      { box: '655', label: 'Claim made by a large company', format: 'flag' },
      { box: '659', label: 'R&D expenditure qualifying for SME R&D relief', format: 'money' },
      { box: '660', label: 'R&D enhanced expenditure', format: 'money' },
    ],
  },
  {
    title: 'Declaration',
    hint: 'The declaration you are signing when you submit.',
    boxes: [
      { box: '975', label: 'Name', format: 'text' },
      { box: '980', label: 'Date', format: 'date' },
      { box: '985', label: 'Status', format: 'text' },
    ],
  },
]

/** Every box number the catalogue knows about. */
export const KNOWN_BOX_NUMBERS = new Set(
  CT600_BOX_SECTIONS.flatMap(section => section.boxes.map(b => b.box))
)

/** Render a raw box value the way the paper CT600 shows it. */
export function formatBoxValue(value: unknown, format: BoxFormat): string {
  if (value === null || value === undefined || value === '') return '—'

  switch (format) {
    case 'money': {
      const num = typeof value === 'number' ? value : parseFloat(String(value))
      if (Number.isNaN(num)) return String(value)
      return new Intl.NumberFormat('en-GB', {
        style: 'currency',
        currency: 'GBP',
        minimumFractionDigits: 2,
      }).format(num)
    }
    case 'date': {
      const parsed = new Date(String(value))
      if (Number.isNaN(parsed.getTime())) return String(value)
      return parsed.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      })
    }
    case 'flag':
      // HMRC marks these boxes with an X rather than true/false.
      return value === 'X' || value === true || value === 'true' ? 'X' : '—'
    case 'number':
      return String(value)
    default:
      return String(value)
  }
}
